import React from "react";
import PropTypes from "prop-types";
//Material imports
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import makeStyles from "@mui/styles/makeStyles";
import Typography from "@mui/material/Typography";
import ListItem from "@mui/material/ListItem";
// Limitless imports
import { useTranslation } from "@limitless/common/hooks";
import TaskListItemLock from "./TaskListItemLock";
import TaskListItemHeader from "./TaskListItemHeader";
import { TASK_TYPE_ICON_MAP } from "../../modern-task-list/modern-task-avatar/ModernTasksAvatar";

const useStyles = makeStyles(theme => ({
    container: {
        position: "relative",
        display: "flex",
        width: "100%",
        height: 105,
        flexDirection: "row",
        borderTop: theme.palette.seperator,
        borderBottom: theme.palette.seperator,
        padding: 0,
        opacity: 0.01,
        top: 25,
        backgroundColor: "#fff"
    },
    selected: {
        backgroundColor: "#D3E1FF"
    },
    taskAppear: {
        top: 0,
        opacity: 1
    },
    avatarContainer: {
        display: "flex",
        alignItems: "center",
        marginBottom: "13px"
    },
    taskIcon: {
        width: 50,
        height: 50,
        margin: "0px 8px 0 8px"
    },
    stickyIcon: {
        backgroundColor: theme.palette.primary.main
    },
    itemIcon: {
        backgroundColor: theme.palette.primary.dark
    },
    newIconClass: {
        width: 20,
        height: 20
    },
    contentWrapper: {
        display: "flex",
        height: "100%",
        flexDirection: "column",
        width: "82%"
    },
    messageWrapper: {
        display: "flex",
        flex: 1,
        width: "100%",
        overflow: "hidden",
        wordWrap: "break-word",
        wordBreak: "break-word",
        marginTop: 4
    },
    taskBody: {
        lineHeight: "1.1em"
    },
    contentFooter: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-end",
        alignItems: "center",
        height: 44
    }
}));

const TaskListItem = ({ message, delay, selected, onRemoveTask }) => {
    const [mounted, setMounted] = React.useState(false);
    const [fadeout, setFadeout] = React.useState(false);

    const classes = useStyles();
    const { t } = useTranslation();

    React.useEffect(() => {
        setMounted(true);
    }, []);

    React.useEffect(() => {
        if (message.claimedByAnother) {
            setFadeout(false);
        }
    }, [message.claimedByAnother]);

    const fadeoutItemHandler = () => {
        setFadeout(true);
    };

    const removeTask = event => {
        if (fadeout && onRemoveTask && event.propertyName === "opacity") {
            onRemoveTask(message.messageId);
        }
    };

    let taskType =
        message.tenantChannel === "forum"
            ? TASK_TYPE_ICON_MAP[`forum${message.type}`]
            : TASK_TYPE_ICON_MAP[message.type];
    taskType = taskType || TASK_TYPE_ICON_MAP["missing"];

    const Icon = taskType.component;

    return (
        <div
            onTransitionEnd={removeTask}
            style={{
                transition: "opacity 100ms ease-out, height 150ms ease-out 200ms",
                height: fadeout ? "0px" : "105px",
                opacity: fadeout ? 0.01 : 1
            }}
        >
            <ListItem
                button
                component="div"
                style={{
                    transition: `top 200ms ease-out ${delay * 0.3}ms, opacity 400ms ease-out ${
                        delay * 0.2
                    }ms, background-color 200ms ease-out`
                }}
                className={`${classes.container} ${mounted ? classes.taskAppear : ""} ${
                    selected ? classes.selected : ""
                }`}
            >
                <div className={classes.avatarContainer}>
                    <Avatar className={`${classes.taskIcon} ${classes[`${taskType.class}`]}`}>
                        <Icon className={`${classes[taskType.iconClass]}`} />
                    </Avatar>
                </div>
                <div className={classes.contentWrapper}>
                    <TaskListItemHeader t={t} message={message} />

                    <div className={classes.messageWrapper}>
                        <Typography className={classes.taskBody} variant="body1" color="textSecondary">
                            {message.body}
                        </Typography>
                    </div>
                    <div className={classes.contentFooter}>
                        {message.rewardFee && message.rewardFee !== "" && (
                            <Chip label={message.rewardFee} size="small" />
                        )}
                    </div>
                </div>
            </ListItem>
            {message.claimedByAnother && <TaskListItemLock onTransitionComplete={fadeoutItemHandler} t={t} />}
        </div>
    );
};

export default TaskListItem;

TaskListItem.propTypes = {
    message: PropTypes.object.isRequired,
    delay: PropTypes.number.isRequired,
    selected: PropTypes.bool,
    onRemoveTask: PropTypes.func
};
